import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  AppShell,
  Button,
  errorMessage,
  formatDate,
  formatDateTime,
  Icon,
  Menu,
  type NavItem,
  type NavSection,
  PRESET_LABELS,
  type ThemePreset,
  useTheme,
  useToast,
} from '@wl/ui';
import type { ReactNode } from 'react';
import { Link, Outlet, useNavigate } from 'react-router';
import { IMPERSONATION_ENDED_PATH, impersonationBannerTitle } from '../components/core-impersonation';
import { MarketStrip } from '../components/invest-status';
import { allowed } from '../lib/access';
import { api } from '../lib/api';
import { ME_KEY, useAuth, useMe } from '../lib/auth';
import { useShellBrand, useSystemStatus } from '../lib/branding';
import type { Me } from '../lib/types';
import { MODULES, SECTION_ORDER, SECTION_TITLES } from './modules';

export function useNav(me: Me): { sections: NavSection[]; tabs: NavItem[] } {
  const visible = MODULES.filter((m) => m.nav !== false && allowed(me, m));
  const sections = SECTION_ORDER.map((s) => ({
    title: SECTION_TITLES[s],
    items: visible.filter((m) => m.section === s).map((m) => ({ to: m.path, label: m.label, icon: m.icon, end: m.end })),
  }));
  const tabs = visible
    .filter((m) => m.mobileTab !== undefined)
    .sort((a, b) => (a.mobileTab ?? 0) - (b.mobileTab ?? 0))
    .map((m) => ({ to: m.path, label: m.label, icon: m.icon, end: m.end }));
  return { sections, tabs };
}

export function AppLayout() {
  const me = useMe();
  const brand = useShellBrand();
  const { sections, tabs } = useNav(me);
  return (
    <AppShell
      brand={brand}
      nav={sections}
      mobileTabs={tabs}
      banner={<Banners me={me} />}
      strip={allowed(me, { perm: 'market.prices' }) ? <MarketStrip /> : null}
      topBar={
        <>
          <ThemeSwitcher />
          <NotificationsBell />
          <UserMenu />
        </>
      }
    >
      <Outlet />
    </AppShell>
  );
}

function Banners({ me }: { me: Me }) {
  const status = useSystemStatus();
  const items: ReactNode[] = [];
  if (me.impersonation) items.push(<ImpersonationBanner key="imp" me={me} />);
  for (const c of status.data?.scheduled ?? []) {
    items.push(
      <Alert key={`s-${c.capability}-${c.startsAt}`} tone="warning" title="Planlı bakım">
        {formatDateTime(c.startsAt)}
        {c.endsAt ? ` – ${formatDateTime(c.endsAt)}` : ''}. {c.message}
      </Alert>,
    );
  }
  for (const c of status.data?.disabled ?? []) {
    items.push(
      <Alert key={`d-${c.capability}`} tone="warning" title="Bazı işlemler geçici olarak kapalı">
        {c.message ?? c.capability}
      </Alert>,
    );
  }
  const sub = me.access.subscription;
  if (sub?.phase === 'grace') {
    items.push(
      <Alert key="grace" tone="danger" title="Aboneliğinizin süresi doldu" action={<Link to="/me/subscription" className="text-sm font-medium underline">Ayrıntılar</Link>}>
        Ek süre {formatDate(sub.graceEndsAt)} tarihinde bitiyor.
      </Alert>,
    );
  }
  if (!items.length) return null;
  return <div className="flex flex-col gap-2 px-3 pt-3 md:px-6">{items}</div>;
}

function ImpersonationBanner({ me }: { me: Me }) {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const imp = me.impersonation!;
  const end = useMutation({
    mutationFn: () => api.post('/auth/impersonation/end'),
    onSuccess: async () => {
      await logout();
      if (imp.actor.type === 'platform_admin') {
        // Panelden açılan sekme kapanır; tarayıcı izin vermezse girişe düşülür.
        window.close();
        window.setTimeout(() => navigate(IMPERSONATION_ENDED_PATH, { replace: true }), 300);
        return;
      }
      navigate(IMPERSONATION_ENDED_PATH, { replace: true });
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
  return (
    <Alert
      tone="accent"
      icon="eye"
      title={impersonationBannerTitle(me)}
      action={
        <Button size="sm" variant="secondary" loading={end.isPending} onClick={() => end.mutate()}>
          Bitir
        </Button>
      }
    >
      Oturum {formatDateTime(imp.expiresAt)} tarihinde sona erer.
    </Alert>
  );
}

function ThemeSwitcher() {
  const { preset, available } = useTheme();
  const qc = useQueryClient();
  const toast = useToast();
  const save = useMutation({
    mutationFn: (themePreset: ThemePreset) => api.put<Pick<Me, 'preferences' | 'theme'>>('/auth/me/preferences', { themePreset }),
    onSuccess: (res) => qc.setQueryData<Me>(ME_KEY, (old) => (old ? { ...old, ...res } : old)),
    onError: (e) => toast.error(errorMessage(e)),
  });
  if (available.length < 2) return null;
  const next = available[(available.indexOf(preset) + 1) % available.length]!;
  return (
    <Button
      variant="ghost"
      size="icon"
      aria-label={`Tema: ${PRESET_LABELS[preset]}. ${PRESET_LABELS[next]} temaya geç`}
      title={`Tema: ${PRESET_LABELS[preset]}`}
      onClick={() => save.mutate(next)}
    >
      <Icon name={preset === 'light' ? 'sun' : 'moon'} size={20} />
    </Button>
  );
}

function NotificationsBell() {
  const q = useQuery({
    queryKey: ['notifications', 'unread-count'],
    queryFn: () => api.get<{ unread: number }>('/notifications', { limit: 1 }),
    refetchInterval: 60_000,
  });
  const unread = q.data?.unread ?? 0;
  return (
    <Link to="/notifications" className="relative rounded p-2 text-foreground hover:bg-surface" aria-label={`Bildirimler${unread ? ` (${unread} okunmamış)` : ''}`}>
      <Icon name="bell" size={20} />
      {unread ? (
        <span className="absolute top-1 right-1 flex min-w-4 items-center justify-center rounded-full bg-danger px-1 text-[10px] leading-4 font-semibold text-white">
          {unread > 99 ? '99+' : unread}
        </span>
      ) : null}
    </Link>
  );
}

function UserMenu() {
  const me = useMe();
  const { logout } = useAuth();
  const navigate = useNavigate();
  const initials = `${me.firstName.charAt(0)}${me.lastName.charAt(0)}`.toUpperCase();
  return (
    <Menu
      trigger={
        <button type="button" className="flex items-center gap-2 rounded-full p-1 hover:bg-surface md:pr-3" aria-label="Hesap menüsü">
          <span className="flex size-8 items-center justify-center rounded-full bg-primary/15 text-xs font-semibold text-primary">{initials}</span>
          <span className="hidden max-w-40 truncate text-sm md:block">
            {me.firstName} {me.lastName}
          </span>
        </button>
      }
      items={[
        { label: 'Profil ve güvenlik', onClick: () => navigate('/me') },
        { label: 'Aboneliğim', onClick: () => navigate('/me/subscription') },
        { label: 'Bildirimler', onClick: () => navigate('/notifications') },
        {
          label: 'Çıkış yap',
          danger: true,
          onClick: () => {
            void logout().then(() => navigate('/auth/login'));
          },
        },
      ]}
    />
  );
}
